import { ENV } from '../../config/runtimeEnv';
import {
	ActionTypeDefinition,
	OperatorDefinition,
	ActionStatistics,
	TestConditionRequest,
	TestActionRequest,
	ValidateActionConfigRequest,
	PaginatedActionLogsResponse,
	ActionLogFilter,
	BulkRetryActionRequest,
	StatisticsFilter
} from "../../types/lab/ProtocolTypes";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_LABIQ_SERVER;

// ===== CONDITIONAL LOGIC MANAGEMENT =====

// Get all available action types with full documentation
export const fetchAvailableActionTypes = async (): Promise<Record<string, ActionTypeDefinition>> => {
	const response = await http.get(
		`${API_BASE_URL}/conditional-logic/actions/types`
	);

	return response.data;
};

// Get available condition operators with documentation
export const fetchAvailableOperators = async (): Promise<Record<string, OperatorDefinition>> => {
	const response = await http.get(
		`${API_BASE_URL}/conditional-logic/conditions/operators`
	);

	return response.data;
};

// Get dynamic option lists for action config fields that have source: 'protocols'
export const fetchActionTypeOptions = async (
	type: string,
	company_id: number
): Promise<{ label: string; value: string | number }[]> => {
	const response = await http.get(
		`${API_BASE_URL}/conditional-logic/options/${type}`,
		{
			params: { company_id },
		}
	);

	return response.data;
};

// Test condition evaluation with detailed feedback
export const testCondition = async (
	request: TestConditionRequest
): Promise<{
	result: boolean;
	message: string;
	condition: any;
	test_data: any;
	evaluation_trace?: any;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/conditional-logic/conditions/test`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data;
};

// Test action execution (dry run or actual execution)
export const testAction = async (
	request: TestActionRequest
): Promise<{
	message: string;
	action?: any;
	result?: any;
	preview?: any;
	dry_run: boolean;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/conditional-logic/actions/test`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data;
};

// Validate action configuration against schema
export const validateActionConfig = async (
	request: ValidateActionConfigRequest
): Promise<{
	valid: boolean;
	errors: Record<string, string[]>;
	action_type: string;
	schema: Record<string, any>;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/conditional-logic/actions/validate`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data;
};

// Get action execution statistics with enhanced filtering
export const fetchActionStatistics = async (
	filters: StatisticsFilter = {}
): Promise<ActionStatistics> => {
	const params = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			params.append(key, value.toString());
		}
	});

	const response = await http.get(
		`${API_BASE_URL}/conditional-logic/statistics/actions?${params.toString()}`
	);

	return response.data;
};

// Get detailed action logs with filtering and pagination
export const fetchActionLogs = async (
	filters: ActionLogFilter = {}
): Promise<PaginatedActionLogsResponse> => {
	const params = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			params.append(key, value.toString());
		}
	});

	const response = await http.get(
		`${API_BASE_URL}/conditional-logic/actions/logs?${params.toString()}`
	);

	return response.data;
};

// Retry failed actions in bulk
export const retryFailedActions = async (
	request: BulkRetryActionRequest
): Promise<{
	message: string;
	total_retries: number;
	results: any[];
}> => {
	const response = await http.post(
		`${API_BASE_URL}/conditional-logic/actions/retry-failed`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data;
};
